import { useCallback, useEffect, useRef, useState } from 'react';
import { Env } from '../../shared';
import { useAdminApi, ApiError } from '../../shared/adminApi';
import {
  CodeFilters,
  CreateCodeBody,
  ReferralCode,
  ReferralCodeDetail,
  ReferralCodesResponse,
  UpdateCodeBody,
} from './types';

/**
 * The referral-codes list (`GET /admin/referrals/codes`).
 *
 * The `from` / `to` filter applies to the AGGREGATE, not to which codes come
 * back: a code with no activity in the window still appears with zeros. That is
 * why the header totals and the row stats both move when the window changes but
 * the row count does not.
 *
 * Filters are compared by value (not identity), so a caller may pass a fresh
 * object literal each render without re-fetching in a loop.
 */
export function useReferralCodes(env: Env, filters: CodeFilters) {
  const { request } = useAdminApi();
  const [data, setData] = useState<ReferralCodesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const filterKey = JSON.stringify(filters);
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const refetch = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const payload = await request<ReferralCodesResponse>({
          env,
          path: '/admin/referrals/codes',
          query: { ...filtersRef.current },
        });
        if (mounted) setData(payload);
      } catch (e) {
        if (mounted) {
          setError(e instanceof ApiError ? e.message : 'Failed to load referral codes');
          setData(null);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
    // filterKey is the value-equality stand-in for `filters`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request, env, filterKey, tick]);

  return { data, loading, error, refetch };
}

/**
 * Per-code detail (`GET /admin/referrals/codes/{code}`) — the same aggregate the
 * list shows (identical SQL server-side, so the two can never disagree) plus the
 * review-flagged rows, a recent-signups tail and the deal grants issued.
 */
export function useReferralCodeDetail(env: Env, code: string, from?: string, to?: string) {
  const { request } = useAdminApi();
  const [data, setData] = useState<ReferralCodeDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const refetch = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const payload = await request<ReferralCodeDetail>({
          env,
          path: `/admin/referrals/codes/${encodeURIComponent(code)}`,
          query: { from, to },
        });
        if (mounted) setData(payload);
      } catch (e) {
        if (mounted) {
          setError(e instanceof ApiError ? e.message : 'Failed to load code detail');
          setData(null);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, [request, env, code, from, to, tick]);

  return { data, loading, error, refetch };
}

/**
 * Mint (`POST`) and edit (`PUT`) referral codes. Both return the written row, so
 * the caller can flash the generated code without a re-fetch.
 *
 * `create` surfaces the backend's own validation text verbatim — it carries the
 * kind rules (promoter needs an owner, campaign must not have one) that the form
 * also enforces client-side.
 */
export function useReferralCodeMutations(env: Env) {
  const { request } = useAdminApi();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const create = useCallback(
    async (body: CreateCodeBody): Promise<ReferralCode | null> => {
      setSaving(true);
      setError(null);
      try {
        return await request<ReferralCode>({
          env,
          path: '/admin/referrals/codes',
          method: 'POST',
          body,
        });
      } catch (e) {
        setError(e instanceof ApiError ? e.message : 'Could not create the code');
        return null;
      } finally {
        setSaving(false);
      }
    },
    [request, env],
  );

  const update = useCallback(
    async (code: string, body: UpdateCodeBody): Promise<ReferralCode | null> => {
      setSaving(true);
      setError(null);
      try {
        return await request<ReferralCode>({
          env,
          path: `/admin/referrals/codes/${encodeURIComponent(code)}`,
          method: 'PUT',
          body,
        });
      } catch (e) {
        setError(e instanceof ApiError ? e.message : 'Could not save the code');
        return null;
      } finally {
        setSaving(false);
      }
    },
    [request, env],
  );

  return { create, update, saving, error, setError };
}

/**
 * Clear a payout-review flag after a human has adjudicated it
 * (`DELETE /admin/referrals/attributions/{id}/review-flag`).
 *
 * Flags are written automatically — device / IP / phone reuse at attribution
 * time, per-code bursts by the nightly fraud job — and a flagged row is held out
 * of the `$` owed aggregate. This is the only way back: without it an honest
 * promoter caught by a shared bar wifi stays unpaid.
 */
export function useClearReviewFlag(env: Env) {
  const { request } = useAdminApi();
  const [clearing, setClearing] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const clearFlag = useCallback(
    async (attributionId: string): Promise<boolean> => {
      setClearing(attributionId);
      setError(null);
      try {
        await request({
          env,
          path: `/admin/referrals/attributions/${encodeURIComponent(attributionId)}/review-flag`,
          method: 'DELETE',
        });
        return true;
      } catch (e) {
        setError(
          e instanceof ApiError
            ? e.code === 'not_flagged'
              ? 'That row is already clear — somebody else got there first.'
              : e.message
            : 'Could not clear the review flag',
        );
        return false;
      } finally {
        setClearing(null);
      }
    },
    [request, env],
  );

  return { clearFlag, clearing, error };
}
